import React, { useState } from "react";
import { Form, Formik, FormikHelpers } from "formik";
import * as Yup from "yup";
import { CircularProgress, Modal } from "@mui/material";

import TextInput from "../../../authentication/reusable/TextInput";
import useTheme from "../../../../common/hooks/useTheme";

import classes from "./InviteClientModal.module.css";
import XIconSvg from "../../../../common/svgs/XIconSvg";
import PrimaryButton from "../../../../common/ui/PrimaryButton";
import { useAuth } from "../../../../providers/AuthProvider";
import { inviteCostumer } from "../../../../common/network/Api";
import { useAppStore } from "../../../../providers/AppStore";

type Props = {
  open: boolean;
  onClose: () => void;
  onClientInvited?: () => void;
};

type InviteDataProps = {
  firstName: string;
  lastName: string;
  email: string;
};

const InviteClientModal = ({ open, onClose, onClientInvited }: Props) => {
  const theme = useTheme();
  const { AuthUser } = useAuth();
  const [errorMessage, setErrorMessage] = useState<string>("");
  const { ASSetOpenSnackBar, ASSetSnackBarMessage, ASSetTypeSnackBar } =
    useAppStore();

  const handleClose = () => {
    setErrorMessage("");
    onClose();
  };

  const handleSubmit = async (
    values: InviteDataProps,
    { setSubmitting }: FormikHelpers<InviteDataProps>
  ) => {
    try {
      const inviteData = {
        role: 1, // Assuming 1 is the role for clients
        email: values.email,
        firstName: values.firstName,
        lastName: values.lastName,
        extendedqn: 0, // Assuming this is a boolean flag
        companyID: AuthUser.pnoe_user_id,
        invitationSender: AuthUser.email,
        origin: "PRECISION", // Assuming this is a constant value for the origin
      };
      await inviteCostumer(inviteData);

      // Trigger the callback to refresh the clients list
      if (onClientInvited) {
        onClientInvited();
      }

      setSubmitting(false);
      handleClose();
      ASSetOpenSnackBar(true);
      ASSetSnackBarMessage({
        title: "",
        message: "Your invitation was sent and is now listed as pending.",
      });
      ASSetTypeSnackBar("success");
    } catch (err) {
      setErrorMessage(
        typeof err === "object" &&
          err &&
          "response" in err &&
          typeof (err as any).response?.data?.messages?.[0]?.message ===
            "string"
          ? (err as any).response.data.messages[0].message
          : "An error occurred while inviting the client."
      );
    }
  };

  return (
    <Modal open={open} onClose={handleClose}>
      <div
        className={classes.modalContainer}
        style={{
          backgroundColor: theme.colors_ds.NEUTRAL_BG_MAIN,
          borderColor: theme.colors_ds.NEUTRAL_BORDER_WEAK,
        }}
      >
        <div className={classes.modalHeaderContainer}>
          <div
            className={classes.modalHeaderTitle}
            style={{ color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT }}
          >
            Invite Client
          </div>
          <div onClick={handleClose} className={classes.iconWrapper}>
            <XIconSvg />
          </div>
        </div>
        <Formik
          initialValues={{
            firstName: "",
            lastName: "",
            email: "",
          }}
          validationSchema={Yup.object({
            firstName: Yup.string()
              .max(15, "Must be 15 characters or less")
              .required("This is a required field."),
            lastName: Yup.string()
              .max(20, "Must be 20 characters or less")
              .required("This is a required field."),
            email: Yup.string()
              .email("Invalid email address")
              .required("This is a required field."),
          })}
          validateOnChange={false}
          validateOnBlur={false}
          onSubmit={handleSubmit}
        >
          {({ isSubmitting }) => (
            <Form className={classes.form}>
              <div className={classes.fieldsContainer}>
                <TextInput label="First Name" name="firstName" type="text" />
                <TextInput label="Last Name" name="lastName" type="text" />
                <TextInput label="Email" name="email" type="email" />
              </div>
              <div className={classes.bottomContainer}>
                {isSubmitting ? (
                  <CircularProgress
                    size={16}
                    sx={{ color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT }}
                  />
                ) : errorMessage ? (
                  <div
                    className={classes.errorMessage}
                    style={{ color: theme.colors_ds.DANGER_GRAPH_DEFICIT }}
                  >
                    {errorMessage}
                  </div>
                ) : null}

                <PrimaryButton
                  type="submit"
                  label="Send"
                  isDisable={isSubmitting}
                />
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </Modal>
  );
};

export default InviteClientModal;
